import {Vector} from './Vector.js';

/**
 * @class
 * @classdesc Прямоугольник
 * @memberof module:math
 */
class Rectangle {

    /**
     * @constructor
     * @param {Number} x x координата центра
     * @param {Number} y y координата центра
     * @param {Number} w ширина
     * @param {Number} h высота
     * @param {Number} angle угол поворота
     */
    constructor(x, y, w, h, angle) {
        /**
         * @member {module:math.Vector} module:math.Rectangle#center центр
         */
        this.center = new Vector(x, y);

        /**
         * @member {Number} module:math.Rectangle~width
         */
        this.width = w;

        /**
         * @member {Number} module:math.Rectangle~height
         */
        this.height = h;

        /**
         * @member {Number} module:math.Rectangle~angle угол поворота
         */
        this.angle = angle;

        /**
         * @member {Array} module:math.Rectangle~vertices массив вершин
         */
        this.vertices = [];

        /**
         * @type Array
         * @@member {Array} module:math.Rectangle~AABB Координаты верхнего левого и нижнего правого угла Axis-Aligned Bounding Box прямоугольника
         */
        this.AABB = [];

        this.refreshVertices();
    }

    /**
     * Клонирование прямоугольника
     * @returns {module:math.Rectangle} копия прямоугольника
     */
    clone() {
        return new Rectangle(this.center.x, this.center.y, this.width, this.height, this.angle);
    }

    /**
     * Пересчет вершин прямоугольника
     */
    refreshVertices() {
        var w = this.width / 2;
        var h = this.height / 2;
        this.vertices = [];
        this.vertices.push(new Vector(-w, h));
        this.vertices.push(new Vector(w, h));
        this.vertices.push(new Vector(w, -h));
        this.vertices.push(new Vector(-w, -h));

        this.AABB = [this.center.clone(), this.center.clone()];

        for (var i = 0; i < 4; i++) {
            this.vertices[i].rotate(-this.angle, this.center);
            if (this.vertices[i].x < this.AABB[0].x) this.AABB[0].x = this.vertices[i].x;
            if (this.vertices[i].x > this.AABB[1].x) this.AABB[1].x = this.vertices[i].x;
            if (this.vertices[i].y < this.AABB[0].y) this.AABB[0].y = this.vertices[i].y;
            if (this.vertices[i].y > this.AABB[1].y) this.AABB[1].y = this.vertices[i].y;
        }
    }

    /**
     * Смещение прямоугольника
     * @param {Number} x смещение по оси x
     * @param {Number} y смещение по оси y
     */
    move(x, y) {
        this.center.add(new Vector(x, y));
        this.refreshVertices();
    }

    /**
     * Поворот прямоугольника
     * @param {Number} angle угол поворота
     */
    rotate(angle) {
        this.angle += angle;
        this.refreshVertices();
    }

    /**
     * Проверка на вхождение точки в прямоугольник
     * @param {module:math.Vector} point точка
     * @returns {Boolean}
     */
    hitTestPoint(point) {
        var p = point.clone();
        p.normalize(-this.angle, this.center);
        return ((Math.abs(p.x) <= (this.width / 2)) && (Math.abs(p.y) <= (this.height / 2)));
    }

    /**
     * Проверка на пересечение с другим прямоугольником
     * @param {module:math.Rectangle} rect прямоугольник
     * @returns {Boolean}
     */
    hitTestRectangle(rect) {
        var r1 = this.clone();
        var r2 = rect.clone();
        var len, len1, len2;

        r1.move(-this.center.x, -this.center.y);
        r2.move(-this.center.x, -this.center.y);
        r2.center.rotate(this.angle);
        r1.rotate(-this.angle);
        r2.rotate(-this.angle);
        len = Math.max(r1.AABB[0].x, r1.AABB[1].x, r2.AABB[0].x, r2.AABB[1].x) - Math.min(r1.AABB[0].x, r1.AABB[1].x, r2.AABB[0].x, r2.AABB[1].x);
        len1 = r1.AABB[1].x - r1.AABB[0].x;
        len2 = r2.AABB[1].x - r2.AABB[0].x;
        if (len > len1 + len2) return false;

        len = Math.max(r1.AABB[0].y, r1.AABB[1].y, r2.AABB[0].y, r2.AABB[1].y) - Math.min(r1.AABB[0].y, r1.AABB[1].y, r2.AABB[0].y, r2.AABB[1].y);
        len1 = r1.AABB[1].y - r1.AABB[0].y;
        len2 = r2.AABB[1].y - r2.AABB[0].y;
        if (len > len1 + len2) return false;

        r1.move(-r2.center.x, -r2.center.y);
        r2.move(-r2.center.x, -r2.center.y);
        r1.center.rotate(r2.angle);
        r1.refreshVertices();
        r1.rotate(-r2.angle);
        r2.rotate(-r2.angle);

        len = Math.max(r1.AABB[0].x, r1.AABB[1].x, r2.AABB[0].x, r2.AABB[1].x) - Math.min(r1.AABB[0].x, r1.AABB[1].x, r2.AABB[0].x, r2.AABB[1].x);
        len1 = r1.AABB[1].x - r1.AABB[0].x;
        len2 = r2.AABB[1].x - r2.AABB[0].x;
        if (len > len1 + len2) return false;

        len = Math.max(r1.AABB[0].y, r1.AABB[1].y, r2.AABB[0].y, r2.AABB[1].y) - Math.min(r1.AABB[0].y, r1.AABB[1].y, r2.AABB[0].y, r2.AABB[1].y);
        len1 = r1.AABB[1].y - r1.AABB[0].y;
        len2 = r2.AABB[1].y - r2.AABB[0].y;

        return (len <= len1 + len2);
    }

}

export {Rectangle};